import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Plane, Star, ArrowRight, Loader2 } from "lucide-react";
import { apiHotelSuggest, apiSearchHotels } from "@/api";
import { formatINR } from "@/mock";

// Shown on flight results: suggests live hotels in the destination city.
// Self-contained: renders nothing while idle/failed so it never breaks the page.
export const HotelCrossSell = ({ city, checkIn, adults = 1 }) => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!city || !checkIn) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const sug = await apiHotelSuggest(city);
        const c = (sug.suggestions || []).find((s) => s.kind === "city") || sug.suggestions?.[0];
        if (!c || c.kind === "hotel" && !c.regionId) { if (active) setHotels([]); return; }
        const ci = checkIn;
        const co = new Date(new Date(checkIn).getTime() + 2 * 864e5).toISOString().slice(0, 10);
        const payload = { checkIn: ci, checkOut: co, rooms: 1, adults: Math.min(Math.max(adults, 1), 4), children: 0 };
        if (c.kind === "hotel") payload.hotelId = c.hotelId; else payload.cityRegionId = c.regionId;
        const res = await apiSearchHotels(payload);
        if (active && res.source === "tripjack") setHotels((res.hotels || []).slice(0, 4));
      } catch { if (active) setHotels([]); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [city, checkIn, adults]);

  if (!loading && hotels.length === 0) return null;
  return (
    <section data-testid="crosssell-hotels" className="mt-8 rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-[#F3F6FF] p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-[Poppins] text-base font-extrabold text-[#1B1F5B]">
            <BedDouble className="h-5 w-5 text-[#0E7C6B]" /> Complete your trip — hotels in {city}
          </h3>
          <p className="text-xs text-slate-400">Live prices · same travel dates · free cancellation on select stays</p>
        </div>
        <Link to="/hotel-page" data-testid="crosssell-hotels-viewall"
          className="flex items-center gap-1 rounded-full bg-[#1B1F5B] px-4 py-2 text-xs font-extrabold text-white hover:bg-[#141844]">
          View all hotels <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-xs font-semibold text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Finding live hotels in {city}...
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {hotels.map((h) => (
            <Link key={h.hotelId} to="/hotel-page" data-testid={`crosssell-hotel-${h.hotelId}`}
              className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
              <div className="h-32 overflow-hidden bg-slate-100">
                {(h.image || h.images?.[0]) && (
                  <img src={h.image || h.images[0]} alt={h.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                )}
              </div>
              <div className="p-3">
                <p className="truncate text-sm font-bold text-slate-800">{h.name}</p>
                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400">
                  {h.star && (<><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {h.star}-star · </>)}
                  {[h.city, h.country].filter(Boolean).join(", ") || city}
                </div>
                <p className="mt-1.5 text-sm font-extrabold text-[#1B1F5B]">
                  {formatINR(h.pricePerNight || h.price)}<span className="text-[11px] font-normal text-slate-400"> /night</span>
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

// Shown on hotel page: nudges the user to also book a flight to the same city.
export const FlightCrossSell = ({ city }) => {
  if (!city) return null;
  const name = String(city).split(",")[0];
  return (
    <div data-testid="crosssell-flights" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#1B1F5B] to-[#2A2F7A] px-5 py-4 text-white shadow-md">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
          <Plane className="h-5 w-5 text-[#F5B32B]" />
        </span>
        <div>
          <p className="text-sm font-extrabold">Need a flight to {name} too?</p>
          <p className="text-xs text-white/60">Compare live fares from all airlines before you book your stay</p>
        </div>
      </div>
      <Link to="/" data-testid="crosssell-flights-btn"
        className="flex items-center gap-1.5 rounded-full bg-[#F5B32B] px-5 py-2.5 text-xs font-extrabold text-[#1B1F5B] hover:bg-[#e0a41f]">
        Search flights <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
};
